import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Ornament } from "../components/Ornament";
import { storytellerDisplayName } from "../lib/storyteller";

/** PROMPT 4 PART 2 — Two-tab review after a conversation finishes. */
export default function ConversationReview() {
  const { day: dayParam } = useParams();
  const day = Number(dayParam);
  const [params] = useSearchParams();
  const convId = params.get("conv");
  const navigate = useNavigate();
  const { user } = useAuth();
  const stName = storytellerDisplayName(user) || "them";

  const [tab, setTab] = useState("clean"); // "clean" | "raw"
  const [conv, setConv] = useState(null);
  const [story, setStory] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");
  const saveTimer = useRef(null);

  useEffect(() => {
    if (!convId) { navigate(`/story-week/day/${day}`, { replace: true }); return; }
    let cancelled = false;
    let pollTimer = null;
    const load = async () => {
      try {
        const { data } = await api.get(`/conversation/${convId}`);
        if (cancelled) return;
        setConv(data);
        setStory(data.clean_story || "");
        // Keep polling every 3s while the pipeline is still running.  It takes
        // ~5-15s for a 30-second recording, and ~60-120s for a real 5-minute one.
        if (data.status === "processing" || data.status === "recording") {
          pollTimer = setTimeout(load, 3000);
        } else if (data.status === "empty" || data.status === "failed") {
          setError(data.error_message || data.error || "We couldn't turn that recording into a story. Please try again.");
        }
      } catch (e) {
        if (!cancelled) setError("Couldn't load this conversation.");
      }
    };
    load();
    return () => { cancelled = true; if (pollTimer) clearTimeout(pollTimer); };
  }, [convId, day, navigate]);

  // Autosave clean story every 5s of inactivity
  useEffect(() => {
    if (!conv) return;
    if (story === conv.clean_story) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.patch(`/conversation/${convId}`, { clean_story: story });
        setSavedAt(new Date());
      } catch (_) {}
    }, 5000);
    return () => clearTimeout(saveTimer.current);
  }, [story, conv, convId]);

  if (!conv) return <div className="min-h-screen paper flex items-center justify-center"><p className="italic text-[#8C8278]" style={{ fontFamily: "Cormorant Garamond, serif" }}>Opening your conversation…</p></div>;

  // Pipeline still running — show a calm, honest waiting screen instead of
  // an empty tabbed view.
  if (conv.status === "processing" || conv.status === "recording") {
    return (
      <div className="min-h-screen paper flex items-center justify-center px-6" data-testid="conv-review-processing">
        <div className="max-w-lg text-center">
          <p className="label-ui text-[11px] text-[#B8973A] tracking-[0.3em]">WORKING ON IT</p>
          <h1 className="mt-6 text-3xl sm:text-4xl text-[#111D33]" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 300, lineHeight: 1.2 }}>
            Turning your conversation into a chapter{stName && stName !== "them" ? ` of ${stName}'s book` : ""}…
          </h1>
          <p className="mt-6 text-[#1B2A4A]" style={{ fontFamily: "Georgia, serif", fontSize: "17px", lineHeight: 1.7 }}>
            Please leave this page open. Longer conversations can take a minute or two.  We'll open the review as soon as it's ready.
          </p>
          <Ornament className="my-8" />
          <p className="text-[#8C8278] italic" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.05rem" }}>
            Some stories deserve a moment of quiet.
          </p>
        </div>
      </div>
    );
  }

  if (conv.status === "empty" || conv.status === "failed") {
    return (
      <div className="min-h-screen paper flex items-center justify-center px-6" data-testid="conv-review-failed">
        <div className="max-w-lg text-center">
          <p className="label-ui text-[11px] text-[#B8973A] tracking-[0.3em]">SOMETHING WENT QUIET</p>
          <h1 className="mt-6 text-3xl sm:text-4xl text-[#111D33]" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 300, lineHeight: 1.2 }}>
            We couldn't turn this one into a story.
          </h1>
          <p className="mt-6 text-[#1B2A4A]" style={{ fontFamily: "Georgia, serif", fontSize: "17px", lineHeight: 1.7 }}>
            {conv.error_message || conv.error || "The recording came through too quiet or was cut short.  Please try again — a fresh, calm space and the phone close by tends to help."}
          </p>
          <div className="mt-8">
            <Link to={`/story-week/day/${day}/conversation`} className="btn-gold inline-block" style={{ padding: "14px 28px", fontSize: "13px", letterSpacing: "0.2em", minHeight: 52 }} data-testid="conv-review-retry">
              TRY AGAIN
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const duration = Math.round((conv.audio_duration_ms || 0) / 1000);
  const mm = Math.floor(duration / 60);
  const ss = String(duration % 60).padStart(2, "0");

  return (
    <div className="min-h-screen paper" data-testid="conv-review-page">
      <header className="max-w-3xl mx-auto px-6 sm:px-10 py-8">
        <Link to="/my-living-book" className="inline-flex items-center gap-2 text-[#1B2A4A] hover:text-[#B8973A]" style={{ minHeight: 44, fontFamily: "Georgia, serif", fontSize: "15px" }} data-testid="conv-review-back">
          <ChevronLeft size={18} /> Back to My Living Book
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 pb-16">
        <p className="label-ui text-[11px] text-[#B8973A] tracking-[0.35em]">SAVED TO {stName.toUpperCase()}'S LIVING BOOK</p>
        <h1 className="mt-4 text-3xl sm:text-4xl" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 300, color: "#111D33", lineHeight: 1.15 }}>
          Day {day}: {conv.question}
        </h1>
        <p className="mt-4 text-[#8C8278]" style={{ fontFamily: "Georgia, serif", fontSize: "14px" }}>
          {conv.asked_by ? <>As told to <strong>{conv.asked_by}</strong> · </> : null}
          {mm}:{ss} recorded
        </p>
        <Ornament className="my-8" />

        {conv.audio_url && (
          <div className="mb-8">
            <audio controls src={conv.audio_url} className="w-full" data-testid="conv-audio">
              Your browser can't play audio inline.
            </audio>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6" role="tablist" data-testid="conv-tabs">
          <button role="tab" aria-selected={tab === "clean"} onClick={() => setTab("clean")}
                  className={`px-5 py-3 hairline-cream ${tab === "clean" ? "bg-[#B8973A] text-[#FAF7F2]" : "bg-white text-[#1B2A4A] hover:bg-[#F7F1E5]"}`}
                  style={{ borderRadius: "4px 4px 0 0", fontFamily: "Georgia, serif", fontSize: "13px", letterSpacing: "0.15em", minHeight: 44 }}
                  data-testid="conv-tab-clean">
            THEIR STORY
          </button>
          <button role="tab" aria-selected={tab === "raw"} onClick={() => setTab("raw")}
                  className={`px-5 py-3 hairline-cream ${tab === "raw" ? "bg-[#B8973A] text-[#FAF7F2]" : "bg-white text-[#1B2A4A] hover:bg-[#F7F1E5]"}`}
                  style={{ borderRadius: "4px 4px 0 0", fontFamily: "Georgia, serif", fontSize: "13px", letterSpacing: "0.15em", minHeight: 44 }}
                  data-testid="conv-tab-raw">
            THE FULL CONVERSATION
          </button>
        </div>

        {tab === "clean" && (
          <div>
            <textarea value={story} onChange={(e) => setStory(e.target.value)}
                      className="w-full bg-white hairline-cream p-6 text-[#1B2A4A]"
                      style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "18px", lineHeight: 1.8, minHeight: 400, resize: "vertical" }}
                      data-testid="conv-clean-text" />
            <p className="mt-2 text-[#8C8278] label-ui text-[11px] tracking-[0.25em]" data-testid="conv-autosave">
              {savedAt ? `SAVED · ${savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "AUTOSAVES AS YOU TYPE"}
            </p>
          </div>
        )}

        {tab === "raw" && (
          <div className="bg-white hairline-cream p-6 text-[#5C5548] whitespace-pre-wrap"
               style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "16px", lineHeight: 1.8 }}
               data-testid="conv-raw-text">
            {conv.transcript || "(No transcript available.)"}
          </div>
        )}

        {error && <p className="mt-4 text-red-700" role="alert" data-testid="conv-review-error">{error}</p>}
      </main>
    </div>
  );
}
